import { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteHits } from 'react-instantsearch';
import ReactPlayer from 'react-player/vimeo';

import Icon from '@klv/atoms/icon';
import Modal from '@klv/organisms/modal';
import WhatsNewResultItem from '@klv/molecules/whats-new-result-item';
import WhatsNewNoResults from '@klv/molecules/whats-new-no-results';
import type { IWhatsNewUpdate } from '@klv/organisms/whats-new-timeline/types';

import type { IWhatsNewResults, IWhatsNewMonth } from './types';
import * as s from './styles/whats-new-results.module.scss';


const WhatsNewMonth = ({
  backgroundColor,
  lastMonth,
  thisMonth,
  quarterlyRecap,
  quarterlyRecapTitle,
  quarterlyRecapVideo,
}: IWhatsNewMonth ) => {

  const { t } = useTranslation();

  const backgroundStyle = backgroundColor === 'dark' ? s.dark : s.light;

  const [showVideoModal, setShowVideoModal] = useState(false);

  if (quarterlyRecap && quarterlyRecapVideo) {
    return (
      <div className={`${s.quarterlyRecap} ${backgroundStyle}`}>
        <button className={s.quarterlyRecapButton} onClick={() => setShowVideoModal(true)}>
          
          <span className={s.monthText}>
            <span className={s.monthIcon}></span>
            {thisMonth}
          </span>
          <span className={s.quarterlyRecapWatchMessage}>
            {t('organisms_whatsNew_watchQuarterlyRecap')}
            <Icon iconName="PlayCircle" />
          </span>
          
        </button>
        <Modal
          contentPadding="30"
          close={() => setShowVideoModal(false)}
          show={showVideoModal}
          size="constrained"
          backgroundColor="lightSand"
          isMediaModal
          title={quarterlyRecapTitle || ' '}
        >
          <ReactPlayer
            url={quarterlyRecapVideo}
            className={s.video}
            controls
            playing
            loop={false}
            width="100%"
            height="auto"
            config={{ playerOptions:{ texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage') } } }  
          />
        </Modal>
      </div>
    );
  } else if (lastMonth !== thisMonth) { 
    return (
        <div className={`${s.month} ${backgroundStyle}`}>
          <span className={s.monthIcon}></span>
          <span className={s.monthText}>{thisMonth}</span>
        </div>
    );
  } else {
    return (
      <div className={`${s.postDivider} ${backgroundStyle}`}>
        <span className={s.connectorIcon}></span>
      </div>
    );
  }

};
const WhatsNewResults = ({
  whatsNewUpdates,
  backgroundColor,
  ...props
}: IWhatsNewResults) => {
  const { hits, sendEvent } = useInfiniteHits(props);

  const { t } = useTranslation();

  // if there are no hits, return null
  if (!hits.length) return <WhatsNewNoResults/>;
  
  // combine our hit with the record from our GraphQL to get a properly formatted record.
  const results = hits.map((hit) => {
    // get our databaseID from the objectID
    const databaseId = parseInt(hit.objectID.replace(/\D/g, ''));
    const update = whatsNewUpdates.find((record) => record.databaseId === databaseId);
    if (update) {
      update._highlightResult = hit._highlightResult;
      if (update.templatesWhatsNewUpdate.buttons) {
        update.templatesWhatsNewUpdate.buttons[0].action = () => sendEvent('click', hit, 'Hit Clicked');
      }
      return update;
    }
  }).filter(Boolean) as IWhatsNewUpdate[];

  return (
  <div className={s.searchResults} {...props}>
    { results.map((result, index) => {
      const lastMonth = index > 0 ? (new Date(results[index - 1].templatesWhatsNewUpdate.postDate)).toLocaleString(t('langCodes_locale'), { month: 'long' }) : null;
      const thisMonth = (new Date(result.templatesWhatsNewUpdate.postDate)).toLocaleString(t('langCodes_locale'), { month: 'long' });
      return (
        <Fragment key={result.databaseId}>
          <WhatsNewMonth 
            backgroundColor={backgroundColor}
            lastMonth={lastMonth}
            thisMonth={thisMonth}
            quarterlyRecap={!!result?.templatesWhatsNewUpdate?.quarterlyRecap}
            quarterlyRecapTitle={result?.templatesWhatsNewUpdate?.quarterlyRecapTitle}
            quarterlyRecapVideo={result?.templatesWhatsNewUpdate?.quarterlyRecapVideo}
          />
          <WhatsNewResultItem key={result.databaseId} {...result} backgroundColor={backgroundColor} />
        </Fragment>
      );
    }) }
  </div>
  );
};

export default WhatsNewResults;
